import type { ElementFingerprint, MatchResult, ConfidenceLevel } from './types';

/**
 * Configuration for matching algorithm
 */
export interface MatchConfig {
  weights?: {
    id: number;
    dataTestid: number;
    textContent: number;
    attributes: number;
    tagName: number;
    context: number;
  };
  threshold?: number;
}

const DEFAULT_CONFIG: Required<MatchConfig> = {
  weights: {
    id: 0.25,          // High weight for ID
    dataTestid: 0.3,   // Very high weight for data-testid
    textContent: 0.25, // Medium weight for text
    attributes: 0.15,  // Medium weight for other attributes
    tagName: 0.03,     // Low weight for tag name
    context: 0.02,     // Low weight for context
  },
  threshold: 0.5, // 50% minimum confidence
};

/**
 * Normalizes a fingerprint from API format (snake_case) to SDK format (camelCase).
 * The API client should handle this conversion, but this provides a safety net
 * for any fingerprints that slip through unconverted.
 */
function normalizeFingerprint(fp: ElementFingerprint): ElementFingerprint {
  // Cast to unknown first, then to Record to check for snake_case properties from API
  const raw = fp as unknown as Record<string, unknown>;
  const rawAttrs = (fp.attributes ?? raw.attributes ?? {}) as Record<string, unknown>;

  return {
    selector: fp.selector ?? (raw.selector as string) ?? '',
    textContent: fp.textContent ?? (raw.text_content as string) ?? '',
    tagName: fp.tagName ?? (raw.tag_name as string) ?? '',
    ariaRole: fp.ariaRole ?? (raw.aria_role as string | null) ?? null,
    parentContext: fp.parentContext ?? (raw.parent_context as string | null) ?? null,
    siblingContext: fp.siblingContext ?? (raw.sibling_context as string | null) ?? null,
    attributes: {
      id: fp.attributes?.id ?? (rawAttrs.id as string | null) ?? null,
      class: fp.attributes?.class ?? (rawAttrs.class as string | null) ?? null,
      dataTestid: fp.attributes?.dataTestid ?? (rawAttrs.dataTestid as string | null) ?? (rawAttrs.data_testid as string | null) ?? null,
      src: fp.attributes?.src ?? (rawAttrs.src as string | null) ?? null,
      href: fp.attributes?.href ?? (rawAttrs.href as string | null) ?? null,
      alt: fp.attributes?.alt ?? (rawAttrs.alt as string | null) ?? null,
      ariaLabel: fp.attributes?.ariaLabel ?? (rawAttrs.ariaLabel as string | null) ?? (rawAttrs.aria_label as string | null) ?? null,
    },
  };
}

/**
 * Finds the best matching element for a given fingerprint using fuzzy matching
 */
export function findMatch(
  rawFingerprint: ElementFingerprint,
  config?: MatchConfig
): MatchResult | null {
  // Normalize fingerprint to handle any unconverted snake_case from API
  const fingerprint = normalizeFingerprint(rawFingerprint);
  const cfg = {
    weights: { ...DEFAULT_CONFIG.weights, ...config?.weights },
    threshold: config?.threshold ?? DEFAULT_CONFIG.threshold,
  };

  // Step 1: Try exact selector match (100% confidence)
  try {
    const exactMatch = document.querySelector(fingerprint.selector);
    if (exactMatch) {
      return {
        element: exactMatch,
        confidence: 100,
        level: 'high',
        signals: {
          exactSelector: true,
          idMatch: false,
          textSimilarity: 1.0,
          attributeMatch: 1.0,
        },
      };
    }
  } catch {
    // Invalid selector, continue to fuzzy matching
  }

  // Step 2: Try ID/data-attribute match (95% confidence)
  if (fingerprint.attributes.id) {
    const idMatch = document.getElementById(fingerprint.attributes.id);
    if (idMatch) {
      return {
        element: idMatch,
        confidence: 95,
        level: 'high',
        signals: {
          exactSelector: false,
          idMatch: true,
          textSimilarity: calculateTextSimilarity(fingerprint.textContent, idMatch.textContent || ''),
          attributeMatch: 1.0,
        },
      };
    }
  }

  if (fingerprint.attributes.dataTestid) {
    const testIdMatch = document.querySelector(`[data-testid="${fingerprint.attributes.dataTestid}"]`);
    if (testIdMatch) {
      return {
        element: testIdMatch,
        confidence: 95,
        level: 'high',
        signals: {
          exactSelector: false,
          idMatch: true,
          textSimilarity: calculateTextSimilarity(fingerprint.textContent, testIdMatch.textContent || ''),
          attributeMatch: 1.0,
        },
      };
    }
  }

  // Step 3: Fuzzy matching across all elements
  // First try same tag name, then all elements if no match
  let candidates: Element[] = [];

  // Only query by tagName if it's not empty
  if (fingerprint.tagName && fingerprint.tagName.trim()) {
    candidates = Array.from(document.querySelectorAll(fingerprint.tagName.toLowerCase()));
  }

  // If no candidates with same tag (or tagName was empty), search all elements
  if (candidates.length === 0) {
    candidates = Array.from(document.querySelectorAll('*'));
  }

  let bestMatch: { element: Element; score: number; signals: MatchResult['signals'] } | null = null;

  for (const element of candidates) {
    const score = calculateCompositeScore(fingerprint, element, cfg.weights);
    const signals = calculateSignals(fingerprint, element);

    if (!bestMatch || score > bestMatch.score) {
      bestMatch = { element, score, signals };
    }
  }

  // Return best match if above threshold
  if (bestMatch !== null && bestMatch.score >= cfg.threshold) {
    const confidence = Math.round(bestMatch.score * 100);
    return {
      element: bestMatch.element,
      confidence,
      level: getConfidenceLevel(confidence),
      signals: bestMatch.signals,
    };
  }

  return null;
}

/**
 * Calculates composite score for an element
 */
function calculateCompositeScore(
  fingerprint: ElementFingerprint,
  element: Element,
  weights: Required<MatchConfig>['weights']
): number {
  let score = 0;

  // ID match
  if (fingerprint.attributes.id && element.getAttribute('id') === fingerprint.attributes.id) {
    score += weights.id;
  }

  // data-testid match
  if (fingerprint.attributes.dataTestid &&
      element.getAttribute('data-testid') === fingerprint.attributes.dataTestid) {
    score += weights.dataTestid;
  }

  // Text content similarity
  const textSimilarity = calculateTextSimilarity(
    fingerprint.textContent,
    element.textContent || ''
  );
  score += textSimilarity * weights.textContent;

  // Attribute matching (src, href, alt, aria-label)
  const attributeScore = calculateAttributeScore(fingerprint, element);
  score += attributeScore * weights.attributes;

  // Tag name match (with bonus if also high text similarity)
  if (fingerprint.tagName === element.tagName) {
    score += weights.tagName;

    // Bonus: if tag matches AND text is very similar, add extra confidence
    if (textSimilarity > 0.9) {
      score += 0.25; // Boost confidence for matching tag + very similar text
    } else if (textSimilarity > 0.8) {
      score += 0.20; // Boost confidence for matching tag + similar text
    }
  }

  // Context matching
  const contextScore = calculateContextScore(fingerprint, element);
  score += contextScore * weights.context;

  return score;
}

/**
 * Calculates text similarity using simple ratio
 */
function calculateTextSimilarity(text1: string, text2: string): number {
  const normalized1 = text1.trim().toLowerCase();
  const normalized2 = text2.trim().toLowerCase();

  if (normalized1 === normalized2) {
    return 1.0;
  }

  if (!normalized1 || !normalized2) {
    return 0;
  }

  // Simple similarity: ratio of common characters
  const longer = normalized1.length > normalized2.length ? normalized1 : normalized2;
  const shorter = normalized1.length > normalized2.length ? normalized2 : normalized1;

  if (longer.includes(shorter)) {
    return shorter.length / longer.length;
  }

  // Levenshtein distance approximation
  const distance = levenshteinDistance(normalized1, normalized2);
  const maxLength = Math.max(normalized1.length, normalized2.length);
  return 1 - distance / maxLength;
}

/**
 * Calculates Levenshtein distance between two strings
 */
function levenshteinDistance(str1: string, str2: string): number {
  const matrix: number[][] = [];

  for (let i = 0; i <= str2.length; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= str1.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= str2.length; i++) {
    for (let j = 1; j <= str1.length; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }

  return matrix[str2.length][str1.length];
}

/**
 * Calculates attribute matching score
 */
function calculateAttributeScore(fingerprint: ElementFingerprint, element: Element): number {
  let matches = 0;
  let total = 0;

  const attributesToCheck: Array<keyof ElementFingerprint['attributes']> = [
    'src',
    'href',
    'alt',
    'ariaLabel',
  ];

  attributesToCheck.forEach((attr) => {
    const fingerprintValue = fingerprint.attributes[attr];
    if (fingerprintValue) {
      total++;
      const elementValue = attr === 'ariaLabel'
        ? element.getAttribute('aria-label')
        : element.getAttribute(attr);

      if (elementValue === fingerprintValue) {
        matches++;
      }
    }
  });

  return total > 0 ? matches / total : 0;
}

/**
 * Calculates context matching score
 */
function calculateContextScore(fingerprint: ElementFingerprint, element: Element): number {
  let score = 0;
  let checks = 0;

  // Check parent context
  if (fingerprint.parentContext) {
    checks++;
    const parentHeading = getParentHeading(element);
    if (parentHeading && parentHeading.includes(fingerprint.parentContext)) {
      score++;
    }
  }

  return checks > 0 ? score / checks : 0;
}

/**
 * Gets parent heading for context
 */
function getParentHeading(element: Element): string | null {
  let current = element.parentElement;

  while (current && current !== document.body) {
    if (/^H[1-6]$/.test(current.tagName)) {
      return current.textContent?.trim() || null;
    }

    const heading = current.querySelector('h1, h2, h3, h4, h5, h6');
    if (heading) {
      return heading.textContent?.trim() || null;
    }

    current = current.parentElement;
  }

  return null;
}

/**
 * Calculates detailed signals for match result
 */
function calculateSignals(fingerprint: ElementFingerprint, element: Element): MatchResult['signals'] {
  return {
    exactSelector: false,
    idMatch: element.getAttribute('id') === fingerprint.attributes.id ||
             element.getAttribute('data-testid') === fingerprint.attributes.dataTestid,
    textSimilarity: calculateTextSimilarity(fingerprint.textContent, element.textContent || ''),
    attributeMatch: calculateAttributeScore(fingerprint, element),
  };
}

/**
 * Converts numeric confidence to level
 */
function getConfidenceLevel(confidence: number): ConfidenceLevel {
  if (confidence >= 90) return 'high';
  if (confidence >= 70) return 'medium';
  if (confidence >= 50) return 'low';
  return 'orphaned';
}

